/**
 * ============================================
 * ASSET: mp-bundle.js
 * PURPOSE: Handles variant selection and add-to-cart
 *          for custom bundle product sections.
 * ADDED: 2026-03-23 by MeshPlanet
 * ============================================
 */
(function () {
  'use strict';

  var container = document.querySelector('[data-mp-bundle]');
  if (!container) return;

  var items = container.querySelectorAll('[data-bundle-item]');
  var addBtn = container.querySelector('[data-mp-bundle-add]');
  var errorEl = container.querySelector('[data-mp-bundle-error]');

  /**
   * Parse variant JSON for each bundle item.
   */
  function getItemData(item) {
    var variantsEl = item.querySelector('[data-bundle-variants]');
    var optionsEl = item.querySelector('[data-bundle-options]');
    return {
      variants: variantsEl ? JSON.parse(variantsEl.textContent) : [],
      options: optionsEl ? JSON.parse(optionsEl.textContent) : []
    };
  }

  /**
   * Get currently selected options for a bundle item.
   * Returns an array indexed by option position (1-based).
   */
  function getSelectedOptions(item) {
    var selected = {};

    // Color
    var colorBtn = item.querySelector('[data-bundle-color][aria-checked="true"]');
    if (colorBtn) {
      selected[colorBtn.dataset.optionPosition] = colorBtn.dataset.optionValue;
    }

    // Size
    var sizeBtn = item.querySelector('[data-bundle-size][aria-checked="true"]');
    if (sizeBtn) {
      selected[sizeBtn.dataset.optionPosition] = sizeBtn.dataset.optionValue;
    }

    return selected;
  }

  /**
   * Find matching variant from selected options.
   */
  function findVariant(variants, options, selectedMap) {
    return variants.find(function (v) {
      return options.every(function (opt) {
        var selectedValue = selectedMap[opt.position];
        if (!selectedValue) return true;
        return v.options[opt.position - 1] === selectedValue;
      });
    });
  }

  /**
   * Update size button availability based on selected color.
   */
  function updateSizeAvailability(item) {
    var data = getItemData(item);
    var selectedOptions = getSelectedOptions(item);
    var sizeBtns = item.querySelectorAll('[data-bundle-size]');

    sizeBtns.forEach(function (btn) {
      var testMap = Object.assign({}, selectedOptions);
      testMap[btn.dataset.optionPosition] = btn.dataset.optionValue;

      var matchingVariant = findVariant(data.variants, data.options, testMap);
      var isAvailable = matchingVariant && matchingVariant.available;

      btn.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
    });
  }

  /**
   * Update the image when color changes.
   */
  function updateImage(item, variant) {
    if (!variant || !variant.featured_image) return;
    var img = item.querySelector('[data-bundle-item-image]');
    if (!img) return;
    img.src = variant.featured_image;
    img.srcset = '';
  }

  /**
   * Update the price display.
   */
  function updatePrice(item, variant) {
    var index = item.dataset.bundleItemIndex;
    var priceEl = item.querySelector('[data-bundle-item-price="' + index + '"]');
    if (!priceEl || !variant) return;

    var moneyFormat = window.Shopify && window.Shopify.currency && window.Shopify.currency.active
      ? window.Shopify.currency.active
      : '';

    var formattedPrice = formatMoney(variant.price);
    var html = '';

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      html += '<span class="compare-at-price">' + formatMoney(variant.compare_at_price) + '</span>';
    }
    html += '<span class="current-price">' + formattedPrice + '</span>';

    priceEl.innerHTML = html;
  }

  /**
   * Format money using Shopify's money format.
   */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = (cents / 100).toFixed(2);
    var moneyFormat = '{{ amount }}';

    if (window.theme && window.theme.moneyFormat) {
      moneyFormat = window.theme.moneyFormat;
    } else if (window.Shopify && window.Shopify.money_format) {
      moneyFormat = window.Shopify.money_format;
    }

    // Remove trailing zeros
    var formatted = value.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
    // Add thousands separator
    formatted = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return moneyFormat
      .replace('{{amount}}', formatted)
      .replace('{{ amount }}', formatted)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString())
      .replace('{{ amount_no_decimals }}', Math.round(cents / 100).toString())
      .replace('{{amount_with_comma_separator}}', formatted.replace(/,/g, '.').replace('.', ','))
      .replace('{{ amount_with_comma_separator }}', formatted.replace(/,/g, '.').replace('.', ','));
  }

  /**
   * Update low stock badge.
   */
  function updateLowStock(item, variant) {
    var index = item.dataset.bundleItemIndex;
    var badge = item.querySelector('[data-bundle-low-stock="' + index + '"]');
    if (!badge) return;

    if (
      variant &&
      variant.inventory_management === 'shopify' &&
      variant.inventory_policy === 'deny' &&
      variant.inventory_quantity > 0 &&
      variant.inventory_quantity <= 5
    ) {
      badge.textContent = 'Low Stock. ' + variant.inventory_quantity + ' left.';
    } else {
      badge.textContent = '';
    }
  }

  /**
   * Update color name display.
   */
  function updateColorLabel(item) {
    var index = item.dataset.bundleItemIndex;
    var label = item.querySelector('[data-bundle-color-value="' + index + '"]');
    var activeColor = item.querySelector('[data-bundle-color][aria-checked="true"]');
    if (label && activeColor) {
      label.textContent = activeColor.dataset.optionValue;
    }
  }

  /**
   * Handle full update after a selection change.
   */
  function handleSelectionChange(item) {
    var data = getItemData(item);
    var selectedOptions = getSelectedOptions(item);
    var variant = findVariant(data.variants, data.options, selectedOptions);

    updateSizeAvailability(item);
    updatePrice(item, variant);
    updateLowStock(item, variant);
    updateAddButtonState();
  }

  /**
   * Enable/disable add button based on all items having a size selected.
   */
  function updateAddButtonState() {
    if (!addBtn) return;
    var allSelected = true;

    items.forEach(function (item) {
      var sizeBtn = item.querySelector('[data-bundle-size][aria-checked="true"]');
      if (!sizeBtn) {
        allSelected = false;
      }
    });

    addBtn.disabled = !allSelected;
    if (errorEl) errorEl.textContent = '';
  }

  /**
   * Handle color swatch click.
   */
  function onColorClick(e) {
    var btn = e.target.closest('[data-bundle-color]');
    if (!btn) return;

    var item = btn.closest('[data-bundle-item]');
    if (!item) return;

    // Deselect all colors in this item
    item.querySelectorAll('[data-bundle-color]').forEach(function (b) {
      b.setAttribute('aria-checked', 'false');
    });
    btn.setAttribute('aria-checked', 'true');

    updateColorLabel(item);

    // Find variant for image update
    var data = getItemData(item);
    var selectedOptions = getSelectedOptions(item);
    var variant = findVariant(data.variants, data.options, selectedOptions);
    updateImage(item, variant);

    handleSelectionChange(item);
  }

  /**
   * Handle size button click.
   */
  function onSizeClick(e) {
    var btn = e.target.closest('[data-bundle-size]');
    if (!btn || btn.getAttribute('aria-disabled') === 'true') return;

    var item = btn.closest('[data-bundle-item]');
    if (!item) return;

    // Deselect all sizes in this item
    item.querySelectorAll('[data-bundle-size]').forEach(function (b) {
      b.setAttribute('aria-checked', 'false');
    });
    btn.setAttribute('aria-checked', 'true');

    handleSelectionChange(item);
  }

  /**
   * Trigger Horizon's checkmark burst animation on the button.
   * Mirrors the flow in product-form.js: set data-added, wait for
   * animationend on the button, then hold briefly before removing.
   */
  function animateButton() {
    addBtn.setAttribute('data-added', 'true');

    function onEnd() {
      addBtn.removeEventListener('animationend', onEnd);
      setTimeout(function () {
        addBtn.removeAttribute('data-added');
      }, 600);
    }

    addBtn.addEventListener('animationend', onEnd);

    // Fallback in case animationend never fires
    setTimeout(function () {
      addBtn.removeAttribute('data-added');
      addBtn.removeEventListener('animationend', onEnd);
    }, 2000);
  }

  /**
   * Dispatch Horizon's cart:update event so cart icon, drawer, and items update.
   */
  function dispatchCartUpdate(cart) {
    // Collect section IDs from cart-items components for section rendering
    var sectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      sectionIds.push(el.dataset.sectionId);
    });

    // Fetch updated section HTML if there are cart-items components
    var sectionsParam = sectionIds.length > 0 ? '?sections=' + sectionIds.join(',') : '';

    fetch('/cart.js' + sectionsParam)
      .then(function (r) { return r.json(); })
      .then(function (cartData) {
        var sections = {};
        if (cartData.sections) {
          sections = cartData.sections;
        }

        var event = new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartData,
            sourceId: container.id || 'mp-bundle',
            data: {
              source: 'product-form-component',
              itemCount: cartData.item_count,
              productId: container.dataset.mpBundleProductId,
              sections: sections
            }
          }
        });
        document.dispatchEvent(event);
      });
  }

  /**
   * Collect all selected variant IDs and add to cart.
   */
  function onAddToCart() {
    var cartItems = [];
    var valid = true;

    items.forEach(function (item, idx) {
      var data = getItemData(item);
      var selectedOptions = getSelectedOptions(item);
      var variant = findVariant(data.variants, data.options, selectedOptions);

      if (!variant || !variant.available) {
        valid = false;
        return;
      }

      cartItems.push({
        id: variant.id,
        quantity: 1,
        properties: {
          _bundle_id: container.dataset.mpBundleProductId,
          _bundle_item: (idx + 1).toString()
        }
      });
    });

    if (!valid) {
      if (errorEl) errorEl.textContent = 'Please select all options for each item.';
      return;
    }

    addBtn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cartItems })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(function (response) {
        addBtn.disabled = false;

        // Trigger Horizon's checkmark burst animation
        animateButton();

        // Dispatch Horizon's cart:update event for drawer/icon/items sync
        dispatchCartUpdate(response);
      })
      .catch(function (err) {
        addBtn.disabled = false;
        if (errorEl) errorEl.textContent = 'Could not add to cart. Please try again.';
        console.error('MP Bundle add to cart error:', err);
      });
  }

  /**
   * Handle size guide click — open the product's own fit chart dialog.
   */
  function onSizeGuideClick(btn) {
    var index = btn.dataset.bundleSizeGuide;
    var dialog = container.querySelector('[data-bundle-sg-dialog="' + index + '"]');
    if (dialog && dialog.showModal) {
      dialog.showModal();
    }
  }

  /**
   * Handle size guide close.
   */
  function onSizeGuideClose(btn) {
    var index = btn.dataset.bundleSgClose;
    var dialog = container.querySelector('[data-bundle-sg-dialog="' + index + '"]');
    if (dialog && dialog.close) {
      dialog.close();
    }
  }

  // ---- Event listeners (delegation) ----
  container.addEventListener('click', function (e) {
    var sizeGuideBtn = e.target.closest('[data-bundle-size-guide]');
    var sizeGuideClose = e.target.closest('[data-bundle-sg-close]');

    if (sizeGuideBtn) {
      onSizeGuideClick(sizeGuideBtn);
    } else if (sizeGuideClose) {
      onSizeGuideClose(sizeGuideClose);
    } else if (e.target.closest('[data-bundle-color]')) {
      onColorClick(e);
    } else if (e.target.closest('[data-bundle-size]')) {
      onSizeClick(e);
    }
  });

  if (addBtn) {
    addBtn.addEventListener('click', function (e) {
      e.preventDefault();
      onAddToCart();
    });
  }

  // ---- Initialize ----
  items.forEach(function (item) {
    updateSizeAvailability(item);
  });
  updateAddButtonState();
})();
